import { MainTemplate } from 'Components/Template/MainTemplate'
import { Box, CircularProgress, Container, Skeleton, Stack, ThemeProvider } from '@mui/material'
import { observer } from 'mobx-react-lite'
import styled from '@emotion/styled'
import ReferCard from 'Components/Elements/PaymentResponse/ReferCard'
import QuestionCard from 'Components/Elements/PaymentResponse/QuestionCard'
import ThankyouCard from 'Components/Elements/PaymentResponse/ThankyouCard'
import GuideCard from 'Components/Elements/PaymentResponse/GuideCard'
import { useEffect, useMemo } from 'react'
import { FeelGreatAPI } from 'Services/FeelGreat/APIs'
import { useLocation, useNavigate } from 'react-router-dom'
import { useState } from 'react'
import storeCountry from 'GlobalStores/Cores/County/StoreCountry'
import storeReferral from 'GlobalStores/Referral/StoreReferral'
import trackingEvents from 'Services/Tracker/TrackingEvents'
import { trackPurchase } from 'Services/Tracker/DataLayerTrackingEvents'
import { when } from 'mobx'
import storeAuth from 'GlobalStores/User/StoreAuth'
import { PaymentAPIs } from 'GlobalStores/Checkout/PaymentServices/PaymentAPI'
import { get } from 'lodash'
import storeFormat from 'GlobalStores/Format/StoreFormat'
import QuestionCardV2 from 'Components/Elements/PaymentResponse/QuestionCardV2'
import { FormatAPIs } from 'Services/FeelGreat/FormatAPIs'
import StoreProducts from 'GlobalStores/StoreProducts'

const reDirectPaymentCountry = ['PH', 'TW', 'HK']
const reDirect3dsPaymentCountry = ['MA', 'JO', 'EG', 'TH']

const resolveItemName = name => {
    if (!name) return ''
    if (typeof name === 'string') return name
    return name.english || name.native || Object.values(name)[0] || ''
}

const buildTrackingItems = forGaItems => {
    if (Array.isArray(forGaItems) && forGaItems.length > 0) {
        return forGaItems.map(item => ({
            item_id: item.item_id || item.item_code,
            item_name: resolveItemName(item.item_name),
            price: item.price,
            quantity: item.quantity || item.qty || 1
        }))
    }

    const formatProducts = storeFormat.GetFormatData('formatProducts')
    if (Array.isArray(formatProducts) && formatProducts.length > 0) {
        return formatProducts.map(item => ({
            item_id: item.item_code,
            item_name: resolveItemName(item.item_name),
            price: item.baStatus === 'C' ? item.retail_price_x_qty?.value : item.price_x_qty?.value,
            quantity: item.qty || 1
        }))
    }

    return []
}

const isCountryShowQuestionCardV2 = () => {
	const country = ['TR']
	return country.includes(storeCountry.Country2())
}

const PaymentResponseSuccess = observer(() => {
	const location = useLocation()
	const navigate = useNavigate()
	const qs = useMemo(() => new URLSearchParams(window.location.search), [])
	const isInIframe = window.self !== window.top

	const [states, setStates] = useState({
		paymentId: '',
		orderSummary: '',
		email: '',
		orderNumber: '',
		ba_id: '',
		referral_code: '',
		baStatus: '',
		is_subscribe_order: '',
	})

	const [loading, setLoading] = useState(true)

	useEffect(() => {
		// if (isInIframe) {
		// 	window.parent.postMessage({ type: 'WP_3DS_SUCCESS', payment_id: qs.get('payment_id') }, '*')
		// 	return
		// }

		const fetchPaymentDetail = async () => {
			if (reDirectPaymentCountry.includes(storeCountry.Country2())) {
				GetGatewayPaymentDetail()
			} else if (reDirect3dsPaymentCountry.includes(storeCountry.Country2())) {
				GetHydra3DSPaymentDetail()
			} else if (['KW', 'TR', 'SA', 'ZA'].includes(storeCountry.Country2())) {
				if (qs.get('payment_id')) {
					GetPaymentDetail()
				}

				if (qs.get('gateway_ref_id')) {
					GetGatewayPaymentDetail()
				}
			} else {
				GetPaymentDetail()
			}
		}

		fetchPaymentDetail()
	}, [])

	// Default
	const GetPaymentDetail = async () => {
		const paymentId = new URLSearchParams(location.search).get('payment_id')
		const MAX_RETRIES = 5
		const RETRY_INTERVAL_MS = 2000

		setLoading(true)

		const fetchOnce = async () => {
			const response = await FeelGreatAPI.GetPaymentDetail(paymentId)
			if (!get(response, 'data.order_number')) {
				return null
			}
			return response
		}

		let response = null
		for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
			try {
				response = await fetchOnce()
				if (response) break
			} catch (e) {
				if (get(e, 'response.status') === 404) {
					navigate(`/payment-response/failed?payment_id=${paymentId}`, { replace: true })
					return
				}
				console.error(e)
			}
			if (attempt < MAX_RETRIES - 1) {
				await new Promise(resolve => setTimeout(resolve, RETRY_INTERVAL_MS))
			}
		}

		if (!response) {
			setStates(prev => ({
				...prev,
				paymentId: paymentId,
				email: '',
				orderSummary: '',
				orderNumber: paymentId,
				is_subscribe_order: ''
			}))
			setLoading(false)
			return
		}

		setStates(prev => ({
			...prev,
			paymentId: paymentId,
			email: response.data.email,
			orderNumber: response.data.order_number,
			orderSummary: `${response.data.total_price.value} ${response.data.total_price.suffix}`,
			baStatus: get(response.data, 'forEmailJourney.dist_status'),
			is_subscribe_order: response.data.is_subscribe_order
		}))

		trackingEvents.PurchaseEvent({
			transaction_id: paymentId,
			value: response.data.total_price?.value,
			currency: storeCountry.GetCurrencyCode(),
			items: response.data.forGa?.items
		})
		trackPurchase({
			transaction_id: paymentId,
			currency: storeCountry.GetCurrencyCode(),
			value: response.data.total_price?.value,
			items: buildTrackingItems(response.data.forGa?.items)
		})

		setLoading(false)
	}

	// Gateway payment
	const GetGatewayPaymentDetail = async () => {
		try {
			setLoading(true)
			let gatewayRefId = new URLSearchParams(location.search).get('gateway_ref_id')
			const response = await FeelGreatAPI.GatewayPaymentDetail(gatewayRefId)

			setStates({
				...states,
				email: response.data.email,
				ba_id: response.data.ba_id,
				orderNumber: response.data.order_number || gatewayRefId,
				is_subscribe_order: response.data.is_subscribe_order,
				referral_code: response.data.referral_code || ''
			})

			storeReferral.referralCode = response.data.referral_code || ''

			if (['PH'].includes(storeCountry.Country2())) {
				trackingEvents.PurchaseEvent({
					transaction_id: response.data.forGa?.transaction_id,
					value: response.data.forGa?.value,
					currency: response.data.forGa?.currency,
					category: response.data.forGa?.category,
					items: response.data.forGa?.items
				})
			}
			
			const parseForGaItems = (raw) => {
				try {
					const parsed = typeof raw === 'string' ? JSON.parse(raw) : raw
					if (!Array.isArray(parsed)) return []
					return parsed.map(item => {
						const product = StoreProducts.GetProductByItemCode(String(item.item_code))
						const flavorText = product?.flavor?.text
						const itemName = flavorText?.english || flavorText?.native || item.item_name || ''
						return {
							item_id: item.item_code || item.item_id || '',
							item_name: itemName,
							price: item.price,
							quantity: item.qty || item.quantity || 1
						}
					})
				} catch {
					return []
				}
			}

			try {
				await when(() => StoreProducts.GetIsInit(), { timeout: 5000 })
			} catch {
				// StoreProducts not ready within 5s — proceed without item_name
			}

			const forGaItems = parseForGaItems(response.data.forGa?.items)
			trackPurchase({
				transaction_id: response.data.forGa?.transaction_id,
				currency: response.data.forGa?.currency,
				value: Number(response.data.forGa?.value) || 0,
				items: forGaItems
			})

			setLoading(false)
		} catch (e) {
			console.error(e)
			setLoading(false)
		}
	}

	// 3ds hydra payment
	const GetHydra3DSPaymentDetail = async () => {
		const paymentId = new URLSearchParams(location.search).get('payment_id')
		const orderNumberParam = new URLSearchParams(location.search).get('order_number')
		const MAX_RETRIES = 5
		const RETRY_INTERVAL_MS = 2000
		const baId = storeAuth.GetId() || undefined

		setLoading(true)

		// Gateway redirect can lose the payment id (sessionStorage does not survive in every browser) — fall back to the order number from tppPayload
		if ((!paymentId || paymentId === 'null') && orderNumberParam) {
			try {
				await PaymentAPIs.UpdateLogByOrder({
					payment_status: 'success',
					order_number: orderNumberParam
				})

				const orderDetail = await PaymentAPIs.GetOrderDetailByOrderNumber(orderNumberParam)
				const payment = get(orderDetail, 'data.payment', {})
				const format = get(orderDetail, 'data.format', {})

				setStates({
					...states,
					paymentId: payment.payments_id || '',
					email: format.email || '',
					orderSummary: payment.total_price ? `${payment.total_price} ${storeCountry.GetCurrencyCode()}` : '',
					orderNumber: orderNumberParam,
					is_subscribe_order: /subscribe/i.test(format.shipment_packs || '')
				})

				if (orderDetail.status === 200 && get(orderDetail, 'data.logonAs.id')) {
					await storeAuth.AutoLogin(orderDetail.data.logonAs.id)
				}
			} catch (e) {
				console.error(e)
			}

			setLoading(false)
			return
		}

		const fetchOnce = async () => {
			const response = await FeelGreatAPI.GetPaymentDetail(paymentId, baId)
			if (!get(response, 'data.order_number')) {
				return null
			}
			return response
		}

		let response = null
		for (let attempt = 0; attempt < MAX_RETRIES; attempt++) {
			try {
				response = await fetchOnce()
				if (response) break
			} catch (e) {
				if (get(e, 'response.status') === 404) {
					navigate(`/payment-response/failed?payment_id=${paymentId}`, { replace: true })
					return
				}
				console.error(e)
			}
			if (attempt < MAX_RETRIES - 1) {
				await new Promise(resolve => setTimeout(resolve, RETRY_INTERVAL_MS))
			}
		}

		if (!response) {
			setStates({
				...states,
				paymentId: paymentId,
				email: '',
				orderSummary: '',
				orderNumber: '',
				is_subscribe_order: ''
			})
			setLoading(false)
			return
		}

		setStates({
			...states,
			paymentId: paymentId,
			email: response.data.email,
			orderSummary: `${response.data.total_price.value} ${response.data.total_price.suffix}`,
			orderNumber: response.data.order_number,
			is_subscribe_order: response.data.is_subscribe_order
		})

		try {
			// QR does not redirect through the gateway — its log is fired from QRPaymentPage via hydra-log
			if (new URLSearchParams(location.search).get('method') !== 'qr') {
				await PaymentAPIs.UpdateLogByOrder({
					payments_id: paymentId,
					payment_status: 'success',
					order_number: response.data.order_number
				})
			}

			const orderDetail = await PaymentAPIs.GetOrderDetailByOrderNumber(response.data.order_number)
			if (orderDetail.status === 200) {
				await storeAuth.AutoLogin(orderDetail.data.logonAs.id)
			}
		} catch (e) {
			console.error(e)
		}

		setLoading(false)
	}

	if (isInIframe) {
		return (
			<ThemeProvider theme={MainTemplate}>
				<Stack backgroundColor={MainTemplate.palette.background.default} minHeight="100vh" alignItems="center" justifyContent="center">
					<CircularProgress />
				</Stack>
			</ThemeProvider>
		)
	}

	return (
		<ThemeProvider theme={MainTemplate}>
			<Stack backgroundColor={MainTemplate.palette.background.default} minHeight="100vh">
				{loading === false ? (
					<ContainerStyled maxWidth="lg">
						<Stack sx={{ justifyContent: 'center', flexDirection: 'row' }}>
							{/* Desktop */}
							{states.baStatus === 'H' ? (
								<StackStyledHDesktop>
									<ThankyouCard states={states} />
									<GuideCard />
									{isCountryShowQuestionCardV2() ? <QuestionCardV2 /> : <QuestionCard />}
								</StackStyledHDesktop>
							) : (
								<StackStyledDesktop>
									<CardWrapper sx={{ gap: '30px', display: 'grid' }}>
										<ThankyouCard states={states} />
										{isCountryShowQuestionCardV2() ? <QuestionCardV2 /> : <QuestionCard />}
									</CardWrapper>
									<CardWrapper sx={{ gap: '30px' }}>
										<GuideCard />
										<ReferCard states={states} />
									</CardWrapper>
								</StackStyledDesktop>
							)}

							{/* Mobile */}
							<StackStyledMobile>
								<Stack sx={{ gap: '30px' }}>
									<ThankyouCard states={states} redirectPaymentCountry={reDirectPaymentCountry} />
									<GuideCard />
								</Stack>
								<Stack sx={{ gap: '30px' }}>
									{isCountryShowQuestionCardV2() ? <QuestionCardV2 /> : <QuestionCard />}
									{states.baStatus === 'H' ? <></> : <ReferCard states={states} />}
								</Stack>
							</StackStyledMobile>
						</Stack>
					</ContainerStyled>
				) : (
					<>
						{/* <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 'calc(100vh - 138px)', boxSizing: 'border-box' }}>
							<CircularProgress />
						</Box> */}
						<PageSkeletonFormElement />
					</>
				)}
			</Stack>
		</ThemeProvider>
	)
})

const PageSkeletonFormElement = () => (
	<>
		<Stack minHeight="100vh">
			<ContainerStyled maxWidth="lg">
				<Stack sx={{ justifyContent: 'center', flexDirection: 'row' }}>
					<SkeletonStackStyled sx={{ width: '100%', gap: { xs: '30px' } }}>
						<Stack sx={{ gap: '30px' }}>
							<Skeleton
								variant="rounded"
								width={'100%'}
								sx={{ margin: '0', borderRadius: '12px', height: { xs: '207px', sm: '230px', md: '242px' } }}
							/>
							<Skeleton
								variant="rounded"
								width={'100%'}
								sx={{ margin: '0', borderRadius: '12px', height: { xs: '482px', sm: '312px', md: '285px', lg: '402px' } }}></Skeleton>
						</Stack>
						<Stack sx={{ gap: '30px' }}>
							<Skeleton variant="rounded" width={'100%'} sx={{ margin: '0', borderRadius: '12px' }}>
								<QuestionCard />
							</Skeleton>
							<Skeleton
								variant="rounded"
								width={'100%'}
								sx={{ margin: '0', borderRadius: '12px', height: { xs: '396px', sm: '370px', md: '338px', lg: '402px' } }}></Skeleton>
						</Stack>
					</SkeletonStackStyled>
				</Stack>
			</ContainerStyled>
		</Stack>
	</>
)

const SkeletonStackStyled = styled(Stack)`
	padding-top: 30px;
	min-height: 50vh;
	@media screen and (min-width: 1200px) {
		display: grid;
		grid-template-columns: 770px 370px;
		gap: 30px;
	}
`

const StackStyledDesktop = styled(Stack)`
	padding-top: 30px;
	min-height: 50vh;
	gap: 30px;
	display: none;
	@media screen and (min-width: 1200px) {
		display: flex;
	}
`

const StackStyledHDesktop = styled(Stack)`
	padding-top: 30px;
	min-height: 50vh;
	display: none;
	margin-bottom: 30px;
	@media screen and (min-width: 1200px) {
		display: grid;
		grid-template-columns: 770px;
		gap: 30px;
	}
`

const StackStyledMobile = styled(Stack)`
	padding-top: 30px;
	min-height: 50vh;
	gap: 30px;
	margin-bottom: 30px;
	@media screen and (min-width: 1200px) {
		display: none;
	}
`

const CardWrapper = styled(Box)`
	display: grid;
	grid-template-columns: 770px 370px;
`

const ContainerStyled = styled(Container)`
	@media screen and (min-width: 730px) and (max-width: 1200px) {
		padding: 0 70px;
	}
`

export default PaymentResponseSuccess
